import {ReactNode, createContext, useContext, useEffect} from 'react';

import {supabase} from '../lib/utils/supabaseClass';

export interface CreatorMetadata {
  id?: string;
  name: string;
  email?: string;
  platform?: {
    name: string;
    id: string;
    handle: string;
  };
  category?: string;
  followers?: number;
}

export interface RedirectorData {
  name: string;
  referral_code?: string;
  application_type: 'mobile' | 'exchange' | 'shop';
  creator_metadata?: CreatorMetadata;
}

export interface ReferralData {
  link_id: string;
  client_id: string;
  tracking_id?: string;
  timestamp?: string;
}

export const REDIRECTOR_EVENTS = {
  SIGNUP_COMPLETED: 'SIGNUP_COMPLETED',
} as const;

interface RedirectorContextType {
  createReferralLink: (data: RedirectorData) => Promise<string>;
  getFirstReferringParams: () => Promise<Record<string, unknown>>;
  getReferralData: () => ReferralData | null;
  clearReferralData: () => void;
  submitReferralConversion: (referralCode: string, applicationType: 'mobile' | 'exchange' | 'shop') => Promise<boolean>;
  trackReferralSignup: () => Promise<void>;
  parseReferralData: (data: string | null) => ReferralData | null;
}

const RedirectorContext = createContext<RedirectorContextType>({} as RedirectorContextType);

const STORAGE_KEY = 'pendingReferralData';

export const useRedirector = (): RedirectorContextType => {
  const context = useContext(RedirectorContext);

  if (Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useRedirector must be used within a RedirectorProvider');
  }

  return context;
};

interface RedirectorProviderProps {
  children: ReactNode;
}

const parseStoredReferralData = (data: string | null): ReferralData | null => {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch (error) {
    console.error('Error parsing stored referral data:', error);
    return null;
  }
};

export const RedirectorProvider = ({children}: RedirectorProviderProps): React.JSX.Element => {
  useEffect((): void => {
    // Check URL parameters for referral data
    const params = new URLSearchParams(window.location.search);
    const referralData: Record<string, unknown> = {};

    // Extract tracking parameters
    const linkId = params.get('link_id');
    const clientId = params.get('client_id');
    const trackingId = params.get('tracking_id');
    const timestamp = params.get('timestamp');

    if (linkId && clientId) {
      referralData.link_id = linkId;
      referralData.client_id = clientId;
      referralData.tracking_id = trackingId;
      referralData.timestamp = timestamp;

      // Store referral data
      localStorage.setItem(STORAGE_KEY, JSON.stringify(referralData));

      // Clean URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const createReferralLink = async (data: RedirectorData): Promise<string> => {
    try {
      const {data: response, error} = await supabase.functions.invoke('create-referral-code', {
        body: JSON.stringify({
          name: data.name,
          referral_code: data.referral_code,
          application_type: data.application_type,
          creator_metadata: data.creator_metadata,
        }),
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      if (!response) {
        throw new Error('No response from create-referral-code function');
      }

      return `${import.meta.env.VITE_REDIRECTOR_URL}/r/${response.data.campaign.link_id}`;
    } catch (error) {
      console.error('Error creating referral link:', error);
      throw error;
    }
  };

  const getFirstReferringParams = async (): Promise<Record<string, unknown>> => {
    const data = localStorage.getItem(STORAGE_KEY);
    const parsedData = parseStoredReferralData(data);
    return parsedData ? {...parsedData} : {};
  };

  const getReferralData = (): ReferralData | null => parseStoredReferralData(localStorage.getItem(STORAGE_KEY));

  const clearReferralData = () => localStorage.removeItem(STORAGE_KEY);

  const submitReferralConversion = async (
    referralCode: string,
    applicationType: 'mobile' | 'exchange' | 'shop'
  ): Promise<boolean> => {
    try {
      const {data, error} = await supabase.functions.invoke('submit-referral-conversion', {
        body: {
          referral_code: referralCode,
          application_type: applicationType,
        },
      });

      if (error || !data) {
        console.error('Error submitting referral conversion:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error submitting referral conversion:', error);
      return false;
    }
  };

  const trackReferralSignup = async (): Promise<void> => {
    try {
      const referralData = parseStoredReferralData(localStorage.getItem(STORAGE_KEY));

      if (!referralData?.link_id) return;

      const {data, error} = await supabase.functions.invoke('submit-referral-conversion', {
        body: {
          link_id: referralData.link_id,
          application_type: 'mobile',
        },
      });

      if (error || !data) {
        console.error('Error tracking referral signup:', error);
      }
    } catch (error) {
      console.error('Error tracking referral signup:', error);
    }
  };

  const parseReferralData = (data: string | null): ReferralData | null => {
    return parseStoredReferralData(data);
  };

  const contextValue: RedirectorContextType = {
    createReferralLink,
    getFirstReferringParams,
    getReferralData,
    clearReferralData,
    submitReferralConversion,
    trackReferralSignup,
    parseReferralData,
  };

  return <RedirectorContext.Provider value={contextValue}>{children}</RedirectorContext.Provider>;
};

export default RedirectorProvider;
